/*
 * The box that opens on Cancel this visit or Skip this visit (owner, 2026-11-04): one tap on a reason cancels it and
 * saves why. Cancel without a reason still cancels it (saved as "not given"), so the question never blocks anyone. A late
 * cancel (under 24 hours) can note a late fee; that only notes it, nothing is charged or sent to the client. The same box
 * changes the reason on a visit already off (Change reason on the visit page and the Cancels report).
 */
import { useState } from "react";
import {
  CANCEL_BY_WORDS,
  CANCEL_REASON_WORDS,
  CANCEL_REASONS,
  hoursBefore,
  isLateCancel,
  NO_TIME_START,
  toCents,
  type CancelBy,
  type CancelInfo,
  type CancelReason,
  type VisitDetail,
} from "@steward/shared";
import { useVisitCancelReason, useVisitStatus, type CancelAnswer } from "../api/hooks";
import { InputText } from "./fields";
import { money, shortDate } from "./format";
import { Modal, useToast } from "./overlays";
import { Banner, Button, Chip } from "./parts";
import { errText } from "./work";

/** The visit the box is about. */
export interface CancelTarget {
  ref: string;
  date: string;
  startTime: string | null;
  clientName: string;
  /** Set when the day is not known here but the visit is already past (To fix, late visits). */
  past?: boolean;
}

type Ask =
  | { mode: "off"; v: CancelTarget; to: "cancelled" | "skipped"; done?: (d: VisitDetail) => void }
  | { mode: "edit"; v: CancelTarget; info: CancelInfo; done?: (d: VisitDetail) => void };

const PICKS = CANCEL_REASONS.filter((r) => r !== "not_given");

/** Hours from now to the visit's start, by this computer's clock (the server saves its own count). */
function hoursLeft(v: CancelTarget): number {
  return hoursBefore(new Date(`${v.date}T${(v.startTime ?? NO_TIME_START).slice(0, 5)}:00`), new Date());
}

/** "3 hours before", "2 days before", "after the visit day". */
export function whenWords(hours: number | null): string {
  if (hours == null) return "time not kept";
  if (hours < 0) return hours > -24 ? "after it was due" : "after the visit day";
  if (hours < 1) return "under an hour before";
  if (hours < 48) return `${String(hours)} ${hours === 1 ? "hour" : "hours"} before`;
  return `${String(Math.floor(hours / 24))} days before`;
}

/** One line for a visit that is off: "Client asked to skip, by the client, 3 hours before. Late fee $40.00". */
export function cancelLine(info: CancelInfo): string {
  const parts = [CANCEL_REASON_WORDS[info.reason]];
  if (info.by) parts.push(info.by === "client" ? "by the client" : "by us");
  parts.push(whenWords(info.hoursBefore));
  let s = parts.join(", ");
  if (info.note && info.reason !== "not_given") s += `. ${info.note}`;
  if (info.feeCents) s += `. Late fee ${money(info.feeCents)}`;
  return s;
}

export function useCancelSheet() {
  const status = useVisitStatus();
  const change = useVisitCancelReason();
  const toast = useToast();
  const [ask, setAsk] = useState<Ask | null>(null);
  const [by, setBy] = useState<CancelBy | null | undefined>(undefined);
  const [other, setOther] = useState(false);
  const [note, setNote] = useState("");
  const [fee, setFee] = useState("");
  const busy = status.isPending || change.isPending;

  const reset = (a: Ask | null) => {
    setAsk(a);
    setBy(a?.mode === "edit" ? (a.info.by ?? undefined) : undefined);
    setOther(a?.mode === "edit" && a.info.reason === "other");
    setNote(a?.mode === "edit" ? (a.info.note ?? "") : "");
    setFee(a?.mode === "edit" && a.info.feeCents ? (a.info.feeCents / 100).toFixed(2) : "");
  };
  const close = () => reset(null);
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });

  const late = ask ? (ask.mode === "edit" ? ask.info.late : ask.v.past === true || isLateCancel(hoursLeft(ask.v))) : false;

  const send = (reason: CancelReason) => {
    if (!ask) return;
    const feeCents = late && fee.trim() ? toCents(fee.trim()) : null;
    const body: CancelAnswer = {
      reason,
      ...(reason === "other" && note.trim() ? { note: note.trim() } : {}),
      ...(by !== undefined ? { by } : {}),
      ...(late ? { feeCents: feeCents && feeCents > 0 ? feeCents : null } : {}),
    };
    const a = ask;
    if (a.mode === "edit") {
      change.mutate(
        { ref: a.v.ref, ...body },
        {
          onSuccess: (d) => {
            close();
            toast("Reason saved");
            a.done?.(d);
          },
          onError: fail,
        },
      );
      return;
    }
    status.mutate(
      { ref: a.v.ref, status: a.to, ...body },
      {
        onSuccess: (d) => {
          close();
          toast(`${a.to === "cancelled" ? "Canceled" : "Skipped"}${a.v.date ? ` ${shortDate(a.v.date)}` : ""}`, {
            undo: () => status.mutate({ ref: d.ref, status: "scheduled" }),
          });
          a.done?.(d);
        },
        onError: fail,
      },
    );
  };

  const feeOk = !fee.trim() || /^\d{1,4}(\.\d{1,2})?$/.test(fee.trim());
  const title = !ask ? "" : ask.mode === "edit" ? "Why is it off?" : ask.to === "cancelled" ? "Why cancel?" : "Why skip?";

  const sheet = (
    <Modal
      title={title}
      open={ask !== null}
      onClose={close}
      footer={
        <>
          <Button type="tertiary" onClick={close}>
            Back
          </Button>
          {other ? (
            <Button loading={busy} disabled={!feeOk} onClick={() => send("other")}>
              Save
            </Button>
          ) : ask?.mode === "off" ? (
            <Button type="secondary" loading={busy} disabled={!feeOk} onClick={() => send("not_given")}>
              {ask.to === "cancelled" ? "Cancel without a reason" : "Skip without a reason"}
            </Button>
          ) : null}
        </>
      }
    >
      {ask ? (
        <div className="stack">
          <p className="muted small">
            {ask.v.clientName}
            {ask.v.date ? ` on ${shortDate(ask.v.date)}` : ""}. Tap the reason.
          </p>
          <div className="chips" aria-label="Who asked">
            <span className="small muted">Who asked</span>
            {(["client", "us"] as const).map((k) => (
              <Chip key={k} pressed={by === k} onClick={() => setBy(by === k ? undefined : k)}>
                {CANCEL_BY_WORDS[k]}
              </Chip>
            ))}
          </div>
          {late ? (
            <>
              <Banner type="warning">This is a late cancel, under 24 hours before the visit.</Banner>
              <InputText
                label="Late fee, if one applies"
                description="This only notes it. It does not charge their card or text them."
                prefix="$"
                inputMode="decimal"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                error={feeOk ? null : "Type an amount like 40 or 40.00"}
              />
            </>
          ) : null}
          <div className="choice" role="group" aria-label="Reason">
            {PICKS.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={(ask.mode === "edit" && ask.info.reason === r) || (r === "other" && other)}
                disabled={busy || !feeOk}
                onClick={() => (r === "other" ? setOther(true) : send(r))}
              >
                <b>{CANCEL_REASON_WORDS[r]}</b>
              </button>
            ))}
          </div>
          {other ? (
            <InputText label="What happened" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          ) : null}
        </div>
      ) : null}
    </Modal>
  );

  return {
    /** Ask why, then cancel or skip. */
    off: (v: CancelTarget, to: "cancelled" | "skipped", done?: (d: VisitDetail) => void) =>
      reset({ mode: "off", v, to, ...(done ? { done } : {}) }),
    /** Change the reason on a visit already off. */
    edit: (v: CancelTarget, info: CancelInfo, done?: (d: VisitDetail) => void) =>
      reset({ mode: "edit", v, info, ...(done ? { done } : {}) }),
    sheet,
  };
}
